package com.arenaproplay.admin.ui.map

import kotlinx.browser.window

/**
 * Minimal Leaflet wrapper for the club address-picker map: a single map with a
 * FIXED centre pin. The pin stays pinned to the centre and the user pans the map
 * underneath it to position the point; every pan/zoom reports the new centre via
 * the [onMoveEnd] callback. Ported (trimmed to the picker use case) from the
 * product web app's `LeafletMap`.
 *
 * Leaflet itself is loaded from unpkg in `build.gradle.kts` (head block); the
 * globals below are namespaced `__clubMap*` so they never clash with anything.
 */
object LeafletMap {

    /** Create the picker map centred on [lat]/[lng]. Safe to call again to re-init. */
    fun init(containerId: String, lat: Double, lng: Double, zoom: Int = 16) {
        window.asDynamic().__clubMapContainerId = containerId
        window.asDynamic().__clubMapLat = lat
        window.asDynamic().__clubMapLng = lng
        window.asDynamic().__clubMapZoom = zoom

        js(
            """
            (function() {
                var id = window.__clubMapContainerId;
                var lat = window.__clubMapLat;
                var lng = window.__clubMapLng;
                var zoom = window.__clubMapZoom;
                var container = document.getElementById(id);
                if (!container) { console.warn('[LeafletMap] container not found:', id); return; }
                if (typeof L === 'undefined') { console.warn('[LeafletMap] Leaflet not loaded'); return; }

                if (window.__clubMap) {
                    try { window.__clubMap.remove(); } catch(e) {}
                    window.__clubMap = null;
                }
                var oldPin = document.getElementById(id + '-pin');
                if (oldPin) oldPin.remove();

                container.style.position = 'relative';

                // Fixed centre pin — a CSS overlay (pointer-events:none) so the map
                // pans under it; the map centre is the selected point.
                var pin = document.createElement('div');
                pin.id = id + '-pin';
                pin.style.cssText = 'position:absolute;top:50%;left:50%;z-index:999;' +
                    'transform:translate(-50%,-100%);pointer-events:none;' +
                    'width:30px;height:40px;filter:drop-shadow(0 3px 6px rgba(0,0,0,0.4));';
                pin.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="30" height="40">' +
                    '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                    '<circle cx="12" cy="11" r="5" fill="white"/>' +
                    '</svg>';
                container.appendChild(pin);

                // Keep wheel-zoom from scrolling the page behind the map.
                container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });

                var worldBounds = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));
                var map = L.map(id, {
                    zoomControl: true,
                    attributionControl: false,
                    scrollWheelZoom: true,
                    doubleClickZoom: true,
                    touchZoom: true,
                    boxZoom: true,
                    maxBounds: worldBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 2,
                    worldCopyJump: true
                }).setView([lat, lng], zoom);

                L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                    maxZoom: 20,
                    subdomains: 'abcd',
                    noWrap: true,
                    bounds: worldBounds
                }).addTo(map);

                window.__clubMap = map;
                window.__clubMapSkipMove = false;

                map.on('moveend', function() {
                    if (window.__clubMapSkipMove) {
                        window.__clubMapSkipMove = false;
                        return;
                    }
                    var c = map.getCenter();
                    if (typeof window.__clubMapMoveCallback === 'function') {
                        window.__clubMapMoveCallback(c.lat, c.lng);
                    }
                });

                setTimeout(function() { map.invalidateSize(); }, 150);
            })();
            """,
        )
    }

    /** Centre the map on [lat]/[lng] WITHOUT firing the move callback (autocomplete pick). */
    fun setView(lat: Double, lng: Double, zoom: Int = 16) {
        window.asDynamic().__clubMapLat = lat
        window.asDynamic().__clubMapLng = lng
        window.asDynamic().__clubMapZoom = zoom
        js(
            """
            (function() {
                if (!window.__clubMap) return;
                window.__clubMapSkipMove = true;
                window.__clubMap.setView(
                    [window.__clubMapLat, window.__clubMapLng],
                    window.__clubMapZoom
                );
            })();
            """,
        )
    }

    /** Register the Kotlin callback fired with the new centre on every user pan/zoom. */
    fun onMoveEnd(callback: (lat: Double, lng: Double) -> Unit) {
        window.asDynamic().__clubMapMoveCallback = callback
    }

    /** Tear the map down on unmount. */
    fun destroy() {
        js(
            """
            (function() {
                if (window.__clubMap) {
                    try { window.__clubMap.remove(); } catch(e) {}
                    window.__clubMap = null;
                }
                window.__clubMapMoveCallback = null;
                window.__clubMapSkipMove = false;
            })();
            """,
        )
    }
}
